import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, X, Save, ListTodo, ChevronLeft, ChevronRight, AlertTriangle, Trash2, LogOut, Bell, Mic, Square, Menu, Target } from 'lucide-react';
import { AgendaProgressBarVertical } from '../components/AgendaProgressBar';
import AgendaStatCard from '../components/AgendaStatCard';
import AgendaDonut from '../components/AgendaDonut';
import AgendaBars from '../components/AgendaBars';
import { fileToDataUrl } from '../components/AgendaDiaryCard';
import { requestFcmToken } from '../firebaseMessaging';
import AgendaAntiprocrastinacaoSection from '../components/AgendaAntiprocrastinacaoSection';

const MAX_GRAVACAO_SEGUNDOS = 20;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('lumis_user_token')}` });

const STATUS_LABEL = {
  pendente: { label: 'Pendente', color: 'bg-gray-100 text-gray-600' },
  em_andamento: { label: 'Em andamento', color: 'bg-blue-100 text-blue-700' },
  concluida: { label: 'Concluída', color: 'bg-green-100 text-green-700' },
  atrasada: { label: 'Atrasada', color: 'bg-red-100 text-red-700' },
  cancelada: { label: 'Cancelada', color: 'bg-gray-100 text-gray-400' },
};
const PRIORIDADE_LABEL = { baixa: 'Baixa', media: 'Média', alta: 'Alta', urgente: 'Urgente' };
const PRIORIDADE_COLOR = { baixa: 'bg-green-500', media: 'bg-yellow-400', alta: 'bg-orange-500', urgente: 'bg-red-500' };
const CATEGORIA_LABEL = {
  trabalho: 'Trabalho', saude: 'Saúde', financeiro: 'Financeiro', familia: 'Família', estudo: 'Estudos',
  desenvolvimento_pessoal: 'Desenvolvimento Pessoal', exercicios: 'Exercícios', compras: 'Compras', casa: 'Casa',
  viagens: 'Viagens', eventos: 'Eventos', lazer: 'Lazer', alimentacao: 'Alimentação', transporte: 'Transporte',
  networking: 'Networking', contatos: 'Contatos', documentacao: 'Documentação', bancos: 'Bancos',
  investimentos: 'Investimentos', projetos: 'Projetos', marketing: 'Marketing', vendas: 'Vendas',
  atendimento: 'Atendimento', recursos_humanos: 'Recursos Humanos', juridico: 'Jurídico', tecnologia: 'Tecnologia',
  manutencao: 'Manutenção', pets: 'Pets', espiritualidade: 'Espiritualidade', voluntariado: 'Voluntariado',
  aniversarios: 'Aniversários', metas: 'Metas', planejamento: 'Planejamento', tarefas: 'Tarefas',
  lembretes: 'Lembretes', importante: 'Importante', pessoal: 'Pessoal', outros: 'Outros',
};
const PRIORIDADE_COR_HEX = { baixa: '#38bdf8', media: '#22c55e', alta: '#f97316', urgente: '#ef4444' };
const ANTECEDENCIA_LABEL = {
  no_momento: 'No momento do compromisso',
  '15min': '15 minutos antes',
  '30min': '30 minutos antes',
  '1h': '1 hora antes',
  '2h': '2 horas antes',
  '1dia': '1 dia antes',
};
const ANTECEDENCIA_LABEL_2 = Object.fromEntries(Object.entries(ANTECEDENCIA_LABEL).filter(([k]) => k !== '1dia'));
const ABAS = [
  { key: 'pendentes', label: 'Pendentes' },
  { key: 'concluidas', label: 'Concluídas' },
  { key: 'todas', label: 'Todas' },
];
const PERIODOS = [
  { key: 'hoje', label: 'Hoje' },
  { key: 'semana', label: 'Semana' },
  { key: 'mes', label: 'Mês' },
  { key: 'ano', label: 'Ano' },
];

const fmtDate = (s) => { if (!s) return '—'; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
const todayStr = () => new Date().toISOString().slice(0, 10);
const addDays = (dateStr, days) => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const addMonths = (dateStr, months) => {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
};
const emptyForm = () => ({
  titulo: '', descricao: '', data_inicio: todayStr(), data_conclusao_prevista: todayStr(),
  prioridade: 'media', categoria: 'outros', tipo: 'unico',
  duplicar: false, duplicar_frequencia: 'semanal', duplicar_qtde: 4,
  lembrete_ativo: false, whatsapp_lembrete_ativo: false, horario_compromisso: '08:00', lembrete_antecedencia: 'no_momento',
  lembrete_antecedencia_2: null, fcm_token: null,
});

export default function AgendaPage() {
  const navigate = useNavigate();
  const [menuAberto, setMenuAberto] = useState(false);
  const [aba, setAba] = useState('antiprocrastinacao');
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ pendentes: 0, em_andamento: 0, atrasadas: 0, concluidas: 0 });
  const [busca, setBusca] = useState('');
  const [prioridadeFiltro, setPrioridadeFiltro] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [gravandoSegundos, setGravandoSegundos] = useState(0);
  const [transcrevendo, setTranscrevendo] = useState(false);
  const mediaRecorderRef = useRef(null);
  const gravacaoChunksRef = useRef([]);
  const gravacaoTimerRef = useRef(null);
  const [periodoAnalytics, setPeriodoAnalytics] = useState('hoje');
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  const loadAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/agenda/analytics/${periodoAnalytics}`, { headers: authHeaders() });
      setAnalytics(res.data);
    } catch {
      toast.error('Erro ao carregar análises');
    } finally {
      setLoadingAnalytics(false);
    }
  }, [periodoAnalytics]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const [pendingNotifications, setPendingNotifications] = useState([]);
  useEffect(() => {
    axios.get(`${API}/notifications/pendentes`, { headers: authHeaders() })
      .then(res => setPendingNotifications(res.data.notifications))
      .catch(() => {});
  }, []);

  const [trialInfo, setTrialInfo] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: authHeaders() })
      .then(res => setTrialInfo(res.data))
      .catch(() => {});
  }, []);

  const [showWhatsappOnboarding, setShowWhatsappOnboarding] = useState(false);
  const [onboardingWhatsappInput, setOnboardingWhatsappInput] = useState('');
  const [savingOnboardingWhatsapp, setSavingOnboardingWhatsapp] = useState(false);

  useEffect(() => {
    if (trialInfo && !trialInfo.whatsapp && !trialInfo.whatsapp_onboarding_visto) {
      setShowWhatsappOnboarding(true);
    }
  }, [trialInfo]);

  const dismissWhatsappOnboarding = async () => {
    setShowWhatsappOnboarding(false);
    try {
      await axios.put(`${API}/auth/whatsapp-onboarding-visto`, {}, { headers: authHeaders() });
      setTrialInfo(prev => ({ ...prev, whatsapp_onboarding_visto: true }));
    } catch {}
  };

  const handleCadastrarWhatsappOnboarding = async () => {
    if (!onboardingWhatsappInput.trim()) return;
    setSavingOnboardingWhatsapp(true);
    try {
      await axios.put(`${API}/auth/whatsapp`, { whatsapp: onboardingWhatsappInput.trim() }, { headers: authHeaders() });
      setTrialInfo(prev => ({ ...prev, whatsapp: onboardingWhatsappInput.trim() }));
      setShowWhatsappOnboarding(false);
      window.open(`https://wa.me/555481054295?text=${encodeURIComponent('Sou novo(a) usuário(a). Meu nome é: ')}`, '_blank');
      toast.success('WhatsApp salvo!');
    } catch {
      toast.error('Erro ao salvar WhatsApp');
    } finally {
      setSavingOnboardingWhatsapp(false);
    }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const res = await axios.post(`${API}/billing/checkout`, {}, { headers: authHeaders() });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error('Erro ao iniciar a assinatura. Tente novamente.');
      setSubscribing(false);
    }
  };

  const dismissNotification = async () => {
    const current = pendingNotifications[0];
    if (!current) return;
    try {
      await axios.put(`${API}/notifications/${current.id}/marcar-lida`, {}, { headers: authHeaders() });
    } catch {}
    setPendingNotifications(prev => prev.slice(1));
  };

  const loadSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agenda/activities/summary`, { headers: authHeaders() });
      setSummary(res.data);
    } catch { /* não bloqueia a tela principal */ }
  }, []);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = { aba, page, page_size: pageSize };
      if (busca) params.busca = busca;
      if (prioridadeFiltro) params.prioridade = prioridadeFiltro;
      const res = await axios.get(`${API}/agenda/activities`, { params, headers: authHeaders() });
      setActivities(res.data.items);
      setTotal(res.data.total);
    } catch {
      toast.error('Erro ao carregar atividades');
    } finally {
      setLoading(false);
    }
  }, [aba, page, busca, prioridadeFiltro]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadActivities(); }, [loadActivities]);
  useEffect(() => { setPage(1); }, [aba, busca, prioridadeFiltro]);

  const openNew = () => {
    setForm(emptyForm());
    setShowForm(true);
  };

  useEffect(() => {
    if (!showForm) return;
    (async () => {
      const token = await requestFcmToken();
      setForm(prev => ({ ...prev, lembrete_ativo: !!token, fcm_token: token || null, whatsapp_lembrete_ativo: !!trialInfo?.whatsapp }));
    })();
  }, [showForm, trialInfo]);

  const handleTranscreverAudio = async (blob) => {
    setTranscrevendo(true);
    try {
      const audioDataUrl = await fileToDataUrl(blob);
      const res = await axios.post(`${API}/agenda/activities/descricao-audio`, { audio_data_url: audioDataUrl }, { headers: authHeaders() });
      setForm(prev => ({ ...prev, descricao: res.data.descricao }));
      toast.success('Descrição gerada a partir do áudio!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar áudio');
    } finally {
      setTranscrevendo(false);
    }
  };

  const pararGravacao = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const iniciarGravacao = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      gravacaoChunksRef.current = [];
      recorder.ondataavailable = e => { if (e.data.size > 0) gravacaoChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(gravacaoTimerRef.current);
        setGravando(false);
        const blob = new Blob(gravacaoChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        handleTranscreverAudio(blob);
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setGravandoSegundos(0);
      setGravando(true);
      gravacaoTimerRef.current = setInterval(() => {
        setGravandoSegundos(prev => {
          const next = prev + 1;
          if (next >= MAX_GRAVACAO_SEGUNDOS) pararGravacao();
          return next;
        });
      }, 1000);
    } catch (err) {
      toast.error('Não foi possível acessar o microfone');
    }
  };

  const handleCreate = async () => {
    if (form.titulo.trim().length < 5) { toast.error('Título deve ter ao menos 5 caracteres'); return; }
    if (form.descricao.trim().length < 10) { toast.error('Descrição deve ter ao menos 10 caracteres'); return; }
    if (form.data_conclusao_prevista < form.data_inicio) { toast.error('Data de conclusão deve ser igual ou posterior à data de início'); return; }
    if ((form.lembrete_ativo || form.whatsapp_lembrete_ativo) && !form.horario_compromisso) { toast.error('Defina o horário do compromisso'); return; }

    const { duplicar, duplicar_frequencia, duplicar_qtde, ...payload } = form;

    setSaving(true);
    try {
      await axios.post(`${API}/agenda/activities`, { ...payload, cor_etiqueta: PRIORIDADE_COR_HEX[form.prioridade] }, { headers: authHeaders() });

      if (duplicar && duplicar_qtde > 0) {
        const shift = duplicar_frequencia === 'mensal'
          ? (dateStr, i) => addMonths(dateStr, i)
          : (dateStr, i) => addDays(dateStr, i * 7);
        for (let i = 1; i <= duplicar_qtde; i++) {
          await axios.post(`${API}/agenda/activities`, {
            ...payload,
            data_inicio: shift(payload.data_inicio, i),
            data_conclusao_prevista: shift(payload.data_conclusao_prevista, i),
            cor_etiqueta: PRIORIDADE_COR_HEX[form.prioridade],
          }, { headers: authHeaders() });
        }
      }

      toast.success(duplicar && duplicar_qtde > 0 ? `Atividade criada com ${duplicar_qtde} duplicação(ões)!` : 'Atividade criada!');
      setShowForm(false);
      loadActivities();
      loadSummary();
      loadAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar atividade');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (activity) => {
    if (!window.confirm('Deseja realmente excluir esse agendamento?')) return;
    try {
      await axios.delete(`${API}/agenda/activities/${activity.id}`, { headers: authHeaders() });
      toast.success('Atividade excluída!');
      loadActivities();
      loadSummary();
      loadAnalytics();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir atividade');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('lumis_user_token');
    navigate('/login');
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-[#EEF2FF] p-5 space-y-6 pb-10" data-testid="agenda-page">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-outfit font-light text-gray-900">Agenda</h1>
          <div className="flex items-center gap-1">
            <button onClick={handleLogout} title="Sair" className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-xl transition-colors">
              <LogOut size={17} />
            </button>
            <button
              data-testid="btn-menu-agenda"
              onClick={() => setMenuAberto(true)}
              title="Menu"
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-white rounded-xl transition-colors"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-500 font-manrope text-center">Elimine a procrastinação com Organização!</p>

        {trialInfo && !trialInfo.blocked && trialInfo.subscription_status !== 'authorized' && (
          <p className="text-xs text-[#1E3A8A] font-manrope text-center">
            Faltam {trialInfo.trial_days_left} {trialInfo.trial_days_left === 1 ? 'dia' : 'dias'} do seu período gratuito.
          </p>
        )}

        <div className="grid grid-cols-2 gap-2" data-testid="agenda-sistema-toggle">
          <button
            data-testid="toggle-antiprocrastinacao"
            onClick={() => setAba('antiprocrastinacao')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-manrope font-semibold text-sm transition-colors ${aba === 'antiprocrastinacao' ? 'bg-[#1E3A8A] text-white' : 'bg-white text-gray-500 border border-gray-200 hover:text-gray-900'}`}
          >
            <Target size={16} /> Antiprocrastinação
          </button>
          <button
            data-testid="toggle-atividades"
            onClick={() => setAba('pendentes')}
            className={`flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-manrope font-semibold text-sm transition-colors ${aba !== 'antiprocrastinacao' ? 'bg-[#1E3A8A] text-white' : 'bg-white text-gray-500 border border-gray-200 hover:text-gray-900'}`}
          >
            <ListTodo size={16} /> Atividades
          </button>
        </div>
      </div>

      {menuAberto && (
        <div className="fixed inset-0 z-40 flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMenuAberto(false)}
            data-testid="agenda-menu-overlay"
          />
          <div
            className="relative w-72 max-w-[80vw] h-full bg-[#EEF2FF] shadow-xl p-5 space-y-3 overflow-y-auto"
            data-testid="agenda-menu-drawer"
          >
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-outfit font-light text-gray-900">Menu</h2>
              <button onClick={() => setMenuAberto(false)} className="text-gray-500 hover:text-gray-900">
                <X size={20} />
              </button>
            </div>

            <button
              data-testid="btn-meus-planos"
              onClick={() => { setMenuAberto(false); navigate('/planos'); }}
              className="w-full text-left px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors"
            >
              Meus Planos
            </button>
            <button
              onClick={() => { setMenuAberto(false); navigate('/modules'); }}
              className="w-full text-left px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors"
            >
              Desenvolva-se
            </button>
            <button
              data-testid="agenda-shortcut-procrastinacao"
              onClick={() => { setMenuAberto(false); navigate('/chat/37d50ce8-7f0f-4592-bfb2-02ce7d3a83df'); }}
              className="w-full text-left px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors"
            >
              Hábitos para Vencer a Procrastinação
            </button>
            <button
              data-testid="agenda-shortcut-produtividade"
              onClick={() => { setMenuAberto(false); navigate('/chat/01b4964f-246e-4042-b6ff-7116bdbc352f'); }}
              className="w-full text-left px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors"
            >
              Aumentar a Produtividade
            </button>
            <button
              data-testid="btn-suporte"
              onClick={() => {
                setMenuAberto(false);
                window.open(`https://wa.me/+5554981125628?text=${encodeURIComponent('Preciso de saber informações sobre o GuiaLumis')}`, '_blank');
              }}
              className="w-full text-left px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors"
            >
              Suporte
            </button>
            <button
              data-testid="btn-cadastrar-whatsapp-menu"
              onClick={() => { setMenuAberto(false); setShowWhatsappOnboarding(true); }}
              className="w-full text-left px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors"
            >
              Cadastrar Whatsapp
            </button>
          </div>
        </div>
      )}

      {showWhatsappOnboarding && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="whatsapp-onboarding-modal">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4">
            <p className="text-sm text-gray-900 font-manrope">
              Prezado usuário, se você deseja que a sua Agenda lhe avise pelo WhatsApp, é preciso fazer o seguinte:
            </p>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Seu WhatsApp</label>
              <input
                data-testid="input-whatsapp-onboarding"
                type="tel"
                placeholder="Ex: 5511999999999"
                value={onboardingWhatsappInput}
                onChange={e => setOnboardingWhatsappInput(e.target.value)}
                className="w-full bg-[#EEF2FF] border border-gray-300 rounded-xl px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-[#1E3A8A]/60"
              />
            </div>
            <button
              data-testid="btn-cadastrar-whatsapp-onboarding"
              onClick={handleCadastrarWhatsappOnboarding}
              disabled={savingOnboardingWhatsapp || !onboardingWhatsappInput.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors disabled:opacity-50"
            >
              {savingOnboardingWhatsapp ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : null}
              Cadastrar meu Whatsapp
            </button>
            <p className="text-xs text-gray-500 font-manrope">
              Ao clicar, vamos salvar seu número e abrir o WhatsApp com uma mensagem pronta: "Sou novo(a) usuário(a). Meu nome é: ". Após os dois pontos, digite apenas seu primeiro nome e envie.
            </p>
            <p className="text-xs text-gray-500 font-manrope">
              Você precisa adicionar esse novo contato como "Guialumis" na sua lista de contatos. Com isso, nossa IA vai te reconhecer e avisar dos seus compromissos por WhatsApp.
            </p>
            <p className="text-xs text-gray-500 font-manrope">
              Se preferir fazer isso depois, no menu do sistema (ícone ☰ no canto superior direito), você encontrará a opção "Cadastrar Whatsapp".
            </p>
            <button
              data-testid="btn-fechar-whatsapp-onboarding"
              onClick={dismissWhatsappOnboarding}
              className="w-full text-center px-4 py-2 text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Agora não
            </button>
          </div>
        </div>
      )}

      {aba !== 'antiprocrastinacao' && (
        <div className="flex gap-2 border-b border-gray-200 pb-3 overflow-x-auto">
          {ABAS.map(a => (
            <button
              key={a.key}
              data-testid={`agenda-aba-${a.key}`}
              onClick={() => setAba(a.key)}
              className={`px-4 py-2 text-sm font-manrope font-semibold rounded-full transition-colors whitespace-nowrap ${aba === a.key ? 'bg-[#1E3A8A] text-white' : 'bg-white text-gray-500 hover:text-gray-900'}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      {aba === 'antiprocrastinacao' ? (
        <AgendaAntiprocrastinacaoSection />
      ) : (
      <>
      <button
        data-testid="btn-nova-atividade"
        onClick={openNew}
        className="text-left px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope font-medium text-sm transition-colors"
      >
        Nova Atividade
      </button>
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full bg-white border border-gray-300 rounded-full pl-9 pr-3 py-2 text-sm text-black placeholder-gray-400 focus:outline-none focus:border-[#1E3A8A] font-manrope"
            placeholder="Buscar por título ou descrição..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
          />
        </div>
        <select
          className="bg-white border border-gray-300 rounded-full px-3 py-2 text-sm text-gray-900 focus:outline-none focus:border-[#1E3A8A]/60 font-manrope"
          value={prioridadeFiltro}
          onChange={e => setPrioridadeFiltro(e.target.value)}
        >
          <option value="">Todas prioridades</option>
          {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : activities.length === 0 ? (
        <div className="text-center py-16 text-gray-500">
          <ListTodo size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-manrope">Nenhuma atividade encontrada</p>
        </div>
      ) : (
        <div className="bg-white border-2 rounded-2xl overflow-x-auto" style={{ borderColor: '#1E3A8A' }}>
          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-500 text-xs uppercase font-manrope">
              <tr>
                <th className="text-left px-4 py-3 w-40">Atividade</th>
                <th className="text-left px-4 py-3">Prioridade</th>
                <th className="text-left px-4 py-3">Início</th>
                <th className="text-left px-4 py-3">Conclusão</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Progresso</th>
                <th className="text-left px-4 py-3">Preencher Agenda</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {activities.map(a => {
                const sc = STATUS_LABEL[a.status] || STATUS_LABEL.pendente;
                return (
                  <tr
                    key={a.id}
                    data-testid={`agenda-row-${a.id}`}
                    onClick={() => navigate(`/agenda/${a.id}`)}
                    className={`border-t border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors font-manrope ${
                      a.prioridade === 'urgente' ? 'bg-red-500/10 animate-pulse-urgent' : a.status === 'atrasada' ? 'bg-red-500/5' : ''
                    }`}
                  >
                    <td className="px-4 py-3 w-40">
                      <div className="font-medium text-gray-900 truncate" title={a.titulo}>{a.titulo}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2" title={PRIORIDADE_LABEL[a.prioridade] || ''}>
                        <span
                          className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${PRIORIDADE_COLOR[a.prioridade] || 'bg-zinc-500'} ${a.prioridade === 'urgente' ? 'animate-pulse' : ''}`}
                        />
                        <span className="text-gray-500 text-xs">{PRIORIDADE_LABEL[a.prioridade] || '—'}</span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(a.data_inicio)}</td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(a.data_conclusao_prevista)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded-full ${sc.color}`}>{sc.label}</span>
                    </td>
                    <td className="px-4 py-3">
                      <AgendaProgressBarVertical value={a.progresso || 0} />
                    </td>
                    <td className="px-4 py-3">
                      {a.precisa_preencher_hoje ? (
                        <span className="flex items-center gap-1 text-xs text-[#1E3A8A]">
                          <AlertTriangle size={14} />
                          Pendente hoje
                        </span>
                      ) : (
                        <span className="text-zinc-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={e => { e.stopPropagation(); handleDelete(a); }}
                        title="Excluir"
                        className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 font-manrope">
              <span className="text-xs text-gray-500">Página {page} de {totalPages} · {total} atividade(s)</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 text-gray-500 hover:text-gray-900 disabled:opacity-30 border border-gray-300 rounded-lg"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1.5 text-gray-500 hover:text-gray-900 disabled:opacity-30 border border-gray-300 rounded-lg"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: 'pendentes', label: 'Pendentes', value: summary.pendentes, color: 'text-gray-600' },
          { key: 'em_andamento', label: 'Em Andamento', value: summary.em_andamento, color: 'text-blue-600' },
          { key: 'atrasadas', label: 'Atrasadas', value: summary.atrasadas, color: 'text-red-600' },
          { key: 'concluidas', label: 'Concluídas', value: summary.concluidas, color: 'text-green-600' },
        ].map(c => (
          <div key={c.key} className="bg-white border border-gray-200 rounded-2xl p-4">
            <p className="text-xs text-gray-500 font-manrope">{c.label}</p>
            <p className={`text-xl font-bold mt-1 font-outfit text-center ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="space-y-4 pt-2">
        <div>
          <h2 className="text-lg font-outfit font-light text-gray-900">Análises</h2>
          <p className="text-xs text-gray-500 font-manrope mt-0.5">Seu progresso ao longo do tempo</p>
        </div>

        <div className="flex gap-1.5">
          {PERIODOS.map(p => (
            <button
              key={p.key}
              data-testid={`analytics-periodo-${p.key}`}
              onClick={() => setPeriodoAnalytics(p.key)}
              className={`px-3 py-1.5 text-xs font-manrope font-medium rounded-full transition-colors ${periodoAnalytics === p.key ? 'bg-[#1E3A8A]/20 text-[#1E3A8A] border border-[#1E3A8A]/40' : 'bg-white text-gray-500 border border-gray-200 hover:text-gray-900'}`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {loadingAnalytics || !analytics ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-6 h-6 border-2 border-[#1E3A8A] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {analytics.cards.map((c, i) => <AgendaStatCard key={i} {...c} />)}
            </div>

            {analytics.atividades_do_dia && analytics.atividades_do_dia.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-2xl p-4">
                <h3 className="text-sm font-manrope font-semibold text-gray-900 mb-3">Minhas Atividades</h3>
                <div className="space-y-2">
                  {analytics.atividades_do_dia.map((a, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 font-manrope flex-1 truncate">{a.titulo}</span>
                      <div className="w-32 h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#1E3A8A] rounded-full" style={{ width: `${a.progresso}%` }} />
                      </div>
                      <span className="text-xs text-gray-500 font-manrope w-10 text-right">{a.progresso}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <AgendaDonut titulo="Status" data={analytics.status} />
              <AgendaDonut titulo="Prioridades" data={analytics.prioridades} />
              <AgendaDonut titulo="Categorias" data={analytics.categorias} />
            </div>

            <AgendaBars titulo={analytics.barras.titulo} data={analytics.barras.data} />
          </div>
        )}
      </div>
      </>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-gray-300 rounded-2xl p-6 w-full max-w-lg shadow-2xl my-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-outfit font-light text-gray-900">Nova Atividade</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-900">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3 font-manrope">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Título da Atividade *</label>
                <input
                  data-testid="input-agenda-titulo"
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black placeholder-gray-400 text-sm focus:outline-none focus:border-[#1E3A8A]"
                  value={form.titulo}
                  onChange={e => setForm({ ...form, titulo: e.target.value })}
                  placeholder="Ex: Praticar 10 min de respiração"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-gray-500 block">Descrição *</label>
                  <button
                    type="button"
                    data-testid="btn-gravar-audio-descricao"
                    onClick={gravando ? pararGravacao : iniciarGravacao}
                    disabled={transcrevendo}
                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full transition-colors disabled:opacity-50 ${gravando ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-[#1E3A8A] hover:bg-[#172554] text-white'}`}
                  >
                    {gravando ? <Square size={12} /> : <Mic size={12} />}
                    {transcrevendo ? 'Processando áudio...' : gravando ? `Gravando ${gravandoSegundos}s / ${MAX_GRAVACAO_SEGUNDOS}s` : 'Gravar áudio'}
                  </button>
                </div>
                <textarea
                  className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black placeholder-gray-400 text-sm focus:outline-none focus:border-[#1E3A8A] min-h-[80px] resize-y"
                  value={form.descricao}
                  onChange={e => setForm({ ...form, descricao: e.target.value })}
                  placeholder="O que precisa ser feito... (ou grave um áudio)"
                />
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Tipo de Agenda</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                    <input
                      data-testid="radio-tipo-unico"
                      type="radio"
                      name="tipo-agenda"
                      value="unico"
                      checked={form.tipo === 'unico'}
                      onChange={() => setForm({ ...form, tipo: 'unico', data_conclusao_prevista: form.data_inicio })}
                      className="w-4 h-4 accent-[#1E3A8A]"
                    />
                    Compromisso Único
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                    <input
                      data-testid="radio-tipo-monitoramento"
                      type="radio"
                      name="tipo-agenda"
                      value="monitoramento"
                      checked={form.tipo === 'monitoramento'}
                      onChange={() => setForm({ ...form, tipo: 'monitoramento' })}
                      className="w-4 h-4 accent-[#1E3A8A]"
                    />
                    Monitoramento Diário
                  </label>
                </div>
              </div>

              {form.tipo === 'unico' ? (
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Data do Compromisso *</label>
                  <input
                    data-testid="input-data-unico"
                    type="date"
                    className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                    value={form.data_inicio}
                    onChange={e => setForm({ ...form, data_inicio: e.target.value, data_conclusao_prevista: e.target.value })}
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Data de Início *</label>
                    <input
                      type="date"
                      className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                      value={form.data_inicio}
                      onChange={e => setForm({ ...form, data_inicio: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Data de Conclusão *</label>
                    <input
                      type="date"
                      className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                      value={form.data_conclusao_prevista}
                      onChange={e => setForm({ ...form, data_conclusao_prevista: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Prioridade</label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PRIORIDADE_LABEL).map(([k, v]) => (
                    <label key={k} className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                      <input
                        data-testid={`radio-prioridade-${k}`}
                        type="radio"
                        name="prioridade"
                        value={k}
                        checked={form.prioridade === k}
                        onChange={() => setForm({ ...form, prioridade: k })}
                        className="w-4 h-4 accent-[#1E3A8A]"
                      />
                      {v}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 mb-1 block">Categoria</label>
                <select
                  className="w-full bg-[#EEF2FF] border border-gray-300 rounded-xl px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-[#1E3A8A]/60"
                  value={form.categoria}
                  onChange={e => setForm({ ...form, categoria: e.target.value })}
                >
                  {Object.entries(CATEGORIA_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              <div className="border-t border-gray-300 pt-3 space-y-3">
                <div className="flex items-center justify-between text-sm text-gray-900">
                  <span>Lembrete por notificação</span>
                  <span className="text-xs text-gray-500">{form.lembrete_ativo ? 'Ativado' : 'Bloqueado pelo navegador'}</span>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-900">
                  <span>Avisar por WhatsApp</span>
                  <span className="text-xs text-gray-500">{form.whatsapp_lembrete_ativo ? 'Ativado' : 'Cadastre seu WhatsApp'}</span>
                </div>
                {!trialInfo?.whatsapp && (
                  <p className="text-xs text-gray-500">
                    Cadastre seu WhatsApp no menu (ícone ☰) → "Cadastrar Whatsapp" para ativar este aviso.
                  </p>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Horário do compromisso</label>
                    <div className="flex gap-2">
                      <select
                        data-testid="select-horario-hora"
                        className="flex-1 bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                        value={form.horario_compromisso.split(':')[0]}
                        onChange={e => setForm({ ...form, horario_compromisso: `${e.target.value}:${form.horario_compromisso.split(':')[1]}` })}
                      >
                        {Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0')).map(h => <option key={h} value={h}>{h}h</option>)}
                      </select>
                      <select
                        data-testid="select-horario-minuto"
                        className="flex-1 bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                        value={form.horario_compromisso.split(':')[1]}
                        onChange={e => setForm({ ...form, horario_compromisso: `${form.horario_compromisso.split(':')[0]}:${e.target.value}` })}
                      >
                        {['00', '15', '30', '45'].map(m => <option key={m} value={m}>{m}min</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Antecedência do lembrete</label>
                    <div className="flex flex-col gap-2">
                      {Object.entries(ANTECEDENCIA_LABEL).map(([k, v]) => (
                        <label key={k} className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                          <input
                            data-testid={`radio-antecedencia-${k}`}
                            type="radio"
                            name="lembrete_antecedencia"
                            value={k}
                            checked={form.lembrete_antecedencia === k}
                            onChange={() => setForm({
                              ...form,
                              lembrete_antecedencia: k,
                              lembrete_antecedencia_2: k === '1dia' ? (form.lembrete_antecedencia_2 || 'no_momento') : null,
                            })}
                            className="w-4 h-4 accent-[#1E3A8A]"
                          />
                          {v}
                        </label>
                      ))}
                    </div>
                  </div>
                  {form.lembrete_antecedencia === '1dia' && (
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Segundo lembrete (mais próximo do horário)</label>
                      <div className="flex flex-col gap-2">
                        {Object.entries(ANTECEDENCIA_LABEL_2).map(([k, v]) => (
                          <label key={k} className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                            <input
                              data-testid={`radio-antecedencia-2-${k}`}
                              type="radio"
                              name="lembrete_antecedencia_2"
                              value={k}
                              checked={form.lembrete_antecedencia_2 === k}
                              onChange={() => setForm({ ...form, lembrete_antecedencia_2: k })}
                              className="w-4 h-4 accent-[#1E3A8A]"
                            />
                            {v}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-300 pt-3">
                <label className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                  <input
                    data-testid="checkbox-duplicar-atividade"
                    type="checkbox"
                    checked={form.duplicar}
                    onChange={e => setForm({ ...form, duplicar: e.target.checked })}
                    className="w-4 h-4 accent-[#1E3A8A]"
                  />
                  Duplicar esta atividade automaticamente
                </label>

                {form.duplicar && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Frequência</label>
                      <select
                        data-testid="select-duplicar-frequencia"
                        className="w-full bg-[#EEF2FF] border border-gray-300 rounded-xl px-3 py-2 text-gray-900 text-sm focus:outline-none focus:border-[#1E3A8A]/60"
                        value={form.duplicar_frequencia}
                        onChange={e => setForm({ ...form, duplicar_frequencia: e.target.value })}
                      >
                        <option value="semanal">Semanal</option>
                        <option value="mensal">Mensal</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">Quantas duplicações?</label>
                      <input
                        data-testid="input-duplicar-qtde"
                        type="number"
                        min="1"
                        max="52"
                        className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-black text-sm focus:outline-none focus:border-[#1E3A8A]"
                        value={form.duplicar_qtde}
                        onChange={e => setForm({ ...form, duplicar_qtde: Math.max(1, parseInt(e.target.value) || 1) })}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 font-manrope">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-900 border border-gray-300 rounded-full transition-colors">
                Cancelar
              </button>
              <button
                data-testid="btn-salvar-atividade"
                onClick={handleCreate}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full font-medium text-sm transition-colors disabled:opacity-50"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={15} />}
                Criar Atividade
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingNotifications[0] && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-[#1E3A8A]/20 rounded-2xl p-6 w-full max-w-sm shadow-2xl text-center" data-testid="notification-modal">
            <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center mx-auto mb-3">
              <Bell size={18} className="text-[#1E3A8A]" />
            </div>
            <h2 className="text-lg font-outfit font-light text-gray-900 mb-2">{pendingNotifications[0].titulo}</h2>
            <p className="text-sm text-gray-500 font-manrope whitespace-pre-wrap mb-5">{pendingNotifications[0].mensagem}</p>
            <button
              data-testid="notification-dismiss-btn"
              onClick={dismissNotification}
              className="w-full bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full px-6 py-2.5 font-manrope font-medium text-sm transition-colors"
            >
              Entendi
            </button>
          </div>
        </div>
      )}

      {trialInfo?.blocked && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="paywall-modal">
          <div className="bg-white border border-[#1E3A8A]/20 rounded-2xl p-6 w-full max-w-sm shadow-2xl text-center">
            <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center mx-auto mb-3">
              <AlertTriangle size={18} className="text-[#1E3A8A]" />
            </div>
            <h2 className="text-lg font-outfit font-light text-gray-900 mb-2">Seu período gratuito acabou</h2>
            <p className="text-sm text-gray-500 font-manrope mb-5">
              Assine por R$ 19,00/mês pra continuar usando sua Agenda e seus Planos.
            </p>
            <button
              data-testid="paywall-subscribe-btn"
              onClick={handleSubscribe}
              disabled={subscribing}
              className="w-full bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-full px-6 py-2.5 font-manrope font-medium text-sm transition-colors disabled:opacity-50"
            >
              {subscribing ? 'Abrindo checkout...' : 'Assinar — R$ 19,00/mês'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
